import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Button, Card, Input, Popconfirm, Tag, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const PhdComment = ({
  comment,
  isEditing,
  editingText,
  onEditingTextChange,
  onEditStart,
  onEditCancel,
  onEditSave,
  onDelete,
  onToggleResolved,
  canEdit,
  canRemove,
  canResolve,
  isSaving,
  isDeleting,
}) => {
  const { t } = useTranslation();
  const authorLabel =
    comment?.teacher?.full_name ||
    comment?.teacher?.display_name ||
    comment?.teacher?.email ||
    t('Neznámy používateľ');
  const createdLabel = comment?.created_at
    ? dayjs(comment.created_at).format('DD.MM.YYYY HH:mm')
    : '—';

  return (
    <Card size="small" className="phd-thesis-detail__comment-card">
      <div className="phd-thesis-detail__comment-header">
        <div className="phd-thesis-detail__comment-author">{authorLabel}</div>
        <div className="phd-thesis-detail__comment-date">
          {createdLabel}
          <Tag color={comment?.is_resolved ? 'green' : 'orange'}>
            {comment?.is_resolved ? t('Vyriešený') : t('Nevyriešený')}
          </Tag>
        </div>
      </div>
      {isEditing ? (
        <div className="phd-thesis-detail__comment-editor">
          <Input.TextArea
            value={editingText}
            onChange={(event) => onEditingTextChange(event.target.value)}
            rows={3}
          />
          <div className="phd-thesis-detail__comment-actions phd-thesis-detail__comment-actions--start">
            <Button
              type="primary"
              size="small"
              onClick={() => onEditSave(comment.id)}
              loading={isSaving}
            >
              {t('Uložiť')}
            </Button>
            <Button size="small" onClick={onEditCancel}>
              {t('Zrušiť')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="phd-thesis-detail__comment-body">{comment.text}</div>
      )}
      {(canEdit || canRemove || canResolve) && (
        <div className="phd-thesis-detail__comment-actions">
          {canResolve && (
            <Tooltip
              title={
                comment?.is_resolved
                  ? t('Označiť ako nevyriešený')
                  : t('Označiť ako vyriešený')
              }
            >
              <Button
                size="small"
                icon={
                  comment?.is_resolved ? (
                    <UndoOutlined />
                  ) : (
                    <CheckCircleOutlined />
                  )
                }
                onClick={() => onToggleResolved(comment)}
              />
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip title={t('Upraviť')}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEditStart(comment)}
              />
            </Tooltip>
          )}
          {canRemove && (
            <Popconfirm
              title={t('Naozaj chcete vymazať komentár?')}
              onConfirm={() => onDelete(comment.id)}
              okText={t('Áno')}
              cancelText={t('Nie')}
            >
              <Tooltip title={t('Vymazať')}>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={isDeleting}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      )}
    </Card>
  );
};

PhdComment.propTypes = {
  comment: PropTypes.shape({
    id: PropTypes.number.isRequired,
    text: PropTypes.string,
    created_at: PropTypes.string,
    phd_teacher_id: PropTypes.number,
    teacher: PropTypes.shape({
      full_name: PropTypes.string,
      display_name: PropTypes.string,
      email: PropTypes.string,
    }),
  }).isRequired,
  isEditing: PropTypes.bool,
  editingText: PropTypes.string,
  onEditingTextChange: PropTypes.func.isRequired,
  onEditStart: PropTypes.func.isRequired,
  onEditCancel: PropTypes.func.isRequired,
  onEditSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggleResolved: PropTypes.func.isRequired,
  canEdit: PropTypes.bool,
  canRemove: PropTypes.bool,
  canResolve: PropTypes.bool,
  isSaving: PropTypes.bool,
  isDeleting: PropTypes.bool,
};

PhdComment.defaultProps = {
  isEditing: false,
  editingText: '',
  canEdit: false,
  canRemove: false,
  canResolve: false,
  isSaving: false,
  isDeleting: false,
};

export default PhdComment;
